import { requiredValidate, getHoverRow } from '../utils';
import { PARENT_ROW_KEY } from '@stores/cardstore/constants';
import { PageStatusType } from '@stores/pagestore';
/* ********************************* 卡片页行操作 *************************************** */
/**
 * 增行&行插入下级节点
 * @param rootStore
 * @param options {
 *  // 必填字段
 *  tableStoreKey: string，默认值：tableStore
 *  //  选填字段
 *  pagingStoreKey: string，默认值：pagingStore
 * }
 * @hooks {
 *  // 专有钩子
 *  // 公共钩子
 *  beforeHook: 开始钩子
 *  afterHook: 结束钩子
 *  catchHook: 异常钩子
 *  finallyHook: 最终钩子
 * }
 */
export const insertChildRow = {
  get visible() {
    const { tableStoreKey } = this.params
    const tableStore = this.rootStore.get(tableStoreKey);
    return tableStore?.uiState === PageStatusType.EDIT || tableStore?.uiState === PageStatusType.ADD
  },
  get enable() {
    return this.visible
  },
  async doAction(rootStore: any, options: any = {}) {
    requiredValidate.call(this, options, ['tableStoreKey']);
    const { utils = {} } = rootStore;
    
    const [hoverRow, tableStore] = getHoverRow.apply(this, arguments);
    if (hoverRow) {
      if (!hoverRow.getValue('id')) {
        utils.message.warning('目前无法在临时行数据上插入下级');
        return Promise.reject('目前无法在临时行数据上插入下级');
      }
      const emptyRowData = tableStore.createEmptyRowData();
      tableStore.addNode(emptyRowData, hoverRow);
    } else {
      tableStore.addEmptyRow().setReadOnly(false);
    }
  }
}

/**
 * 增行&行插入
 * @param rootStore
 * @param options {
 *  // 必填字段
 *  tableStoreKey: string，默认值：tableStore
 *  //  选填字段
 *  pagingStoreKey: string，默认值：pagingStore
 *  uiState: string
 * }
 * @hooks {
 *  // 专有钩子
 *  // 公共钩子
 *  beforeHook: 开始钩子
 *  afterHook: 结束钩子
 *  catchHook: 异常钩子
 *  finallyHook: 最终钩子
 * }
 */
export const insertRow = {
  get visible() {
    const { tableStoreKey } = this.params
    const tableStore = this.rootStore.get(tableStoreKey);
    return tableStore?.uiState === PageStatusType.EDIT || tableStore?.uiState === PageStatusType.ADD
  },
  get enable() {
    return this.visible
  },
  async doAction(rootStore: any, options: any = {}) {
    requiredValidate.call(this, options, ['tableStoreKey']);
    const { uiState } = options;
    
    const [hoverRow, tableStore] = getHoverRow.apply(this, arguments);
    if (hoverRow) {
      if (tableStore.insertNode) { // TreeTableStore
        const emptyRowData = tableStore.createEmptyRowData();
        tableStore.insertNode(emptyRowData, hoverRow);
       } else {
         const hoverRowIndex = tableStore.findIndexByRowKey(hoverRow.rowKey);
         const emptyRowData = tableStore.createEmptyRowData();
         tableStore.insertRow(hoverRowIndex + 1, emptyRowData, options.parentTableStoreKey ? hoverRow.data[PARENT_ROW_KEY] : '');
      }
    } else {
      if (Object.values(PageStatusType).includes(uiState)) {
        await tableStore.setUiState(uiState);
      }
      tableStore.addEmptyRow().setReadOnly(false);
    }

    // 关联分页
    const { pagingStoreKey = 'pagingStore' } = options;
    const pagingStore = rootStore.get(pagingStoreKey);
    if (options.pagingStoreKey) {
      if (!pagingStore) throw (new Error(`The Store[${pagingStoreKey}] not found`));
    }
    if (pagingStore) pagingStore.setPageInfo({ total: pagingStore.total + 1 });
  }
}


/**
 * 侧滑增行&侧滑编辑
 * @param rootStore
 * @param options {
 *  // 必填字段
 *  tableStoreKey: string，默认值：tableStore
 * }
 */
export const swipeInsertRow = {
  async doAction(
    rootStore: any,
    options: any = {},
    event: any,
    eventOptions: any
  ) {
    requiredValidate.call(this, options, ["tableStoreKey"]);
    const [hoverRow, tableStore] = getHoverRow.apply(this, [
      rootStore,
      options,
      event,
      eventOptions,
    ]);
    const { tableStoreKey = "tableStore" } = options;
    if (hoverRow) {
      // 编辑行
      tableStore.isShowModal = true;
      tableStore.onRowByModal({
        tableStoreKey,
        rowStore: hoverRow,
        status: "edit",
      });
    } else {
      // 增行
      tableStore.isShowModal = true;
      tableStore.onRowByModal({
        tableStoreKey,
        status: "add",
      });
    }
  },
};

/**
 * 删行&行删除
 * @param rootStore
 * @param options {
 *  // 必填字段
 *  tableStoreKey: string，默认值：tableStore
 *  //  选填字段
 *  pagingStoreKey: string，默认值：pagingStore
 * }
 * @hooks {
 *  // 专有钩子
 *  // 公共钩子
 *  beforeHook: 开始钩子
 *  afterHook: 结束钩子
 *  catchHook: 异常钩子
 *  finallyHook: 最终钩子
 * }
 */
export const deleteRows = {
  get visible() {
    const { tableStoreKey } = this.params
    const tableStore = this.rootStore.get(tableStoreKey);
    return tableStore?.uiState === PageStatusType.EDIT || tableStore?.uiState === PageStatusType.ADD
  },
  get enable() {
    return this.visible
  },
  async doAction(rootStore: any, options: any = {}) {
    requiredValidate.call(this, options, ['tableStoreKey']);
    const { utils = {} } = rootStore;
    const [hoverRow, tableStore] = getHoverRow.apply(this, arguments);

    const { pagingStoreKey = 'pagingStore' } = options;
    const pagingStore = rootStore.get(pagingStoreKey);
    
    if (hoverRow) {
      if (tableStore.deleteNode) { // TreeTableStore
        tableStore.deleteNode(hoverRow);
      } else {
        tableStore.deleteRow(hoverRow);
      }
      // 关联分页
      if (options.pagingStoreKey) {
        if (!pagingStore) throw (new Error(`The Store[${pagingStoreKey}] not found`));
      }
      if (pagingStore) pagingStore.setPageInfo({ total: Math.max(pagingStore.total - 1, 0) });
    } else {
      const selectRows = tableStore.getSelectedRows();
      if (!selectRows.length) {
        utils.message.warning(lang.templateByUuid('UID:P_CORE-YNF-FE_18951C6405B8003E', '目前没有选中行', undefined, { returnStr: true }));
        return Promise.reject(lang.templateByUuid('UID:P_CORE-YNF-FE_18951C6405B8003E', '目前没有选中行', undefined, { returnStr: true }));
      } else {
        selectRows.forEach((row: any) => tableStore.deleteRow(row));
        // 关联分页
        if (options.pagingStoreKey) {
          if (!pagingStore) throw (new Error(`The Store[${pagingStoreKey}] not found`));
        }
        if (pagingStore) pagingStore.setPageInfo({ total: Math.max(pagingStore.total - selectRows.length, 0) });
      }
    }
  }
}

/**
 * 行复制
 * @param rootStore
 * @param options {
 *  // 必填字段
 *  tableStoreKey: string，默认值：tableStore
 *  // 选填字段
 *  pagingStoreKey: string，默认值：pagingStore
 * }
 * @hooks {
 *  // 专有钩子
 *  // 公共钩子
 *  beforeHook: 开始钩子
 *  afterHook: 结束钩子
 *  catchHook: 异常钩子
 *  finallyHook: 最终钩子
 * }
 */
export const copyRow = {
  get visible() {
    const { tableStoreKey } = this.params
    const tableStore = this.rootStore.get(tableStoreKey);
    return tableStore?.uiState === PageStatusType.EDIT || tableStore?.uiState === PageStatusType.ADD
  },
  get enable() {
    return this.visible
  },
  async doAction(rootStore: any, options: any = {}) {
    requiredValidate.call(this, options, ['tableStoreKey']);
    const [hoverRow, tableStore] = getHoverRow.apply(this, arguments);
    if (!hoverRow) throw (new Error(`The hoverRow not found`));
    if (tableStore.insertNode) { // TreeTableStore
      const copyData = hoverRow?.dataEntityToJson()
      const { children, id, ...filteredData} = copyData
      tableStore.insertNode(filteredData, hoverRow);
     } else {
      const hoverRowIndex = tableStore.findIndexByRowKey(hoverRow.rowKey);
      const copyData = hoverRow?.dataEntityToJson()
      const { children, id, ...filteredData} = copyData
       tableStore.insertRow(hoverRowIndex + 1, filteredData);
    }

    // 关联分页
    const { pagingStoreKey = 'pagingStore' } = options;
    const pagingStore = rootStore.get(pagingStoreKey);
    if (options.pagingStoreKey) {
      if (!pagingStore) throw (new Error(`The Store[${pagingStoreKey}] not found`));
    }
    if (pagingStore) pagingStore.setPageInfo({ total: pagingStore.total + 1 });
  }
}

/* ********************************* 卡片页行操作 *************************************** */
